import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  List,
  ListItemButton,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';

import {
  CompanyShareSelection,
  IChildCompany,
} from '../../../dto/MessageSharing.dto';
import { useRqChildCompanies } from '../../../react_query/messages/sharing';
import { getDefaultShareWindowDraft } from './buildShareTargets';

interface MessageVisibilityEditorProps {
  companyId: number;
  value: CompanyShareSelection[];
  onChange: (value: CompanyShareSelection[]) => void;
  allowSchoolTargets?: boolean;
  disabled?: boolean;
  title?: string;
  helperText?: string;
}

const MessageVisibilityEditor: React.FC<MessageVisibilityEditorProps> = ({
  companyId,
  value,
  onChange,
  disabled = false,
  title = 'Share with Child Partners',
}) => {
  const {data: childCompanies, isLoading} = useRqChildCompanies(
    companyId,
    Boolean(companyId),
  );

  // Track whether initial auto-select has already fired
  const hasAutoSelected = useRef(false);

  // Sync company names from API data
  useEffect(() => {
    if (!childCompanies?.length) {
      return;
    }

    let hasChanges = false;
    const nextValue = value.map(selection => {
      const companyName =
        childCompanies.find(company => company.id === selection.companyId)
          ?.name ?? selection.companyName;

      if (companyName === selection.companyName) {
        return selection;
      }

      hasChanges = true;

      return {
        ...selection,
        companyName,
      };
    });

    if (hasChanges) {
      onChange(nextValue);
    }
  }, [childCompanies, onChange, value]);

  const activeChildCompanies = useMemo(
    () => (childCompanies || []).filter(company => company.is_active),
    [childCompanies],
  );

  // Auto-select all active child companies on initial mount only (once)
  useEffect(() => {
    if (
      !hasAutoSelected.current &&
      value.length === 0 &&
      activeChildCompanies.length > 0
    ) {
      hasAutoSelected.current = true;
      onChange(
        activeChildCompanies.map(company => ({
          companyId: company.id,
          companyName: company.name,
          wholeCompany: true,
          selectedStationIds: [],
          selectedSchoolIds: [],
          ...getDefaultShareWindowDraft(),
        })),
      );
    }
  }, [activeChildCompanies, onChange, value.length]);

  const selectedCompanyIds = useMemo(
    () => new Set(value.map(selection => selection.companyId)),
    [value],
  );

  const allSelected =
    activeChildCompanies.length > 0 &&
    value.length === activeChildCompanies.length;

  // Accordion is expanded when not all partners are selected (user has unchecked some)
  const [expanded, setExpanded] = useState(false);

  // Auto-expand when user unchecks a partner (not all selected anymore)
  useEffect(() => {
    if (!allSelected && activeChildCompanies.length > 0 && value.length >= 0) {
      setExpanded(true);
    }
  }, [allSelected, activeChildCompanies.length, value.length]);

  const handleCompanyToggle = useCallback(
    (company: IChildCompany) => {
      if (selectedCompanyIds.has(company.id)) {
        onChange(value.filter(selection => selection.companyId !== company.id));
        return;
      }

      onChange([
        ...value,
        {
          companyId: company.id,
          companyName: company.name,
          wholeCompany: true,
          selectedStationIds: [],
          selectedSchoolIds: [],
          ...getDefaultShareWindowDraft(),
        },
      ]);
    },
    [onChange, selectedCompanyIds, value],
  );

  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      // Deselect all — user doesn't want to share
      onChange([]);
      setExpanded(true);
    } else {
      // Select all
      onChange(
        activeChildCompanies.map(company => ({
          companyId: company.id,
          companyName: company.name,
          wholeCompany: true,
          selectedStationIds: [],
          selectedSchoolIds: [],
          ...getDefaultShareWindowDraft(),
        })),
      );
      setExpanded(false);
    }
  }, [allSelected, activeChildCompanies, onChange]);

  if (isLoading) {
    return (
      <Box>
        <Skeleton height={56} sx={{borderRadius: 1}} />
      </Box>
    );
  }

  if (!activeChildCompanies.length) {
    return null;
  }

  const summaryText = allSelected
    ? `This message will appear on all child partners (${activeChildCompanies.length})`
    : value.length > 0
    ? `This message will appear on ${value.length} of ${activeChildCompanies.length} child partners`
    : 'This message will not be shared with child partners';

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px !important',
        '&:before': {display: 'none'},
        '&.Mui-expanded': {margin: 0},
      }}
      disableGutters>
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{
          minHeight: 56,
          '& .MuiAccordionSummary-content': {
            alignItems: 'center',
            gap: 1,
          },
        }}>
        <Checkbox
          checked={allSelected}
          indeterminate={value.length > 0 && !allSelected}
          onClick={e => {
            e.stopPropagation();
            handleSelectAll();
          }}
          disabled={disabled}
          disableRipple
        />
        <Stack>
          <Typography variant='subtitle2'>{title}</Typography>
          <Typography variant='caption' color='text.secondary'>
            {summaryText}
          </Typography>
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{pt: 0, pb: 2, px: 2}}>
        <List
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            maxHeight: 280,
            overflow: 'auto',
          }}>
          {activeChildCompanies.map(company => {
            const isSelected = selectedCompanyIds.has(company.id);

            return (
              <ListItemButton
                key={company.id}
                selected={isSelected}
                disabled={disabled}
                onClick={() => handleCompanyToggle(company)}
                divider>
                <Checkbox checked={isSelected} disableRipple tabIndex={-1} />
                <ListItemText primary={company.name} />
              </ListItemButton>
            );
          })}
        </List>
      </AccordionDetails>
    </Accordion>
  );
};

export default MessageVisibilityEditor;
